import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Permission } from "./types/Claims";
import { getPermission } from "./utils/SessionManagement";

export const usePermissionGuard = () => {
  const permissions = getPermission();
  // Returns true if the user has all the required permissions

  const hasPermission = useCallback(
    (requiredPermissions?: Permission[]) => {
      if (!requiredPermissions) {
        // if no permission specified, show by default.
        return true;
      }

      for (let i = 0; i < requiredPermissions.length; i++) {
        if (permissions?.includes(requiredPermissions[i])) {
          return true;
        }
      }

      return false;
    },
    [permissions],
  );

  return { hasPermission };
};

export const useFeaturesGuard = () => {
  const [features, setFeatures] = useState(() => {
    return window.sessionStorage.getItem("features");
  });

  // Effect to watch for changes to session storage
  useEffect(() => {
    const handleStorageChange = () => {
      setFeatures(window.sessionStorage.getItem("features"));
    };

    // Add an event listener for changes to sessionStorage
    window.addEventListener("storage", handleStorageChange);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Returns true if the user has all the required permissions
  const hasFeatureEnabled = useCallback(
    (featurePermission: string[] | undefined) => {
      if (featurePermission && features) {
        return featurePermission.every((permission) => features.includes(permission));
      } else {
        return true;
      }
    },
    [features],
  );

  return { hasFeatureEnabled };
};

export interface PermissionGuardProps {
  children: ReactNode;
  permissions?: Permission[];
}

export const PermissionGuard = ({
  children,
  permissions: requiredPermissions,
}: PermissionGuardProps) => {
  const { hasPermission } = usePermissionGuard();

  const authorized = useMemo(() => hasPermission(requiredPermissions), [requiredPermissions]);

  return authorized ? <>{children}</> : null;
};
